import React from 'react';

/**
 * PercentageBar — visual % indicator with status-aware overrides.
 *
 * Closed-deal statuses force the bar to 100% with a distinctive color, since the
 * deal is functionally closed regardless of the underlying collection ratio.
 * Without this override, settled-from-default or refinanced-out deals visually
 * read as "incomplete" (orange/yellow) even though they're done. Mirrors the
 * same blue/amber palette already used elsewhere in the app for these states.
 *
 *   - "Paid in Full (Refinanced)" → 100%, BLUE (closed via refi)
 *   - "Paid in Full / Settled"    → 100%, AMBER (closed via settlement/write-off)
 *   - "Paid in Full"              → falls through to the standard scale
 *     (which renders emerald at 100% — full collection, no concessions)
 *   - All other statuses          → standard collection-ratio scale
 */
const PercentageBar = ({ percentage, status }) => {
  // Closed-by-refi / closed-by-settlement overrides
  if (status === 'Paid in Full (Refinanced)') {
    return (
      <div className="flex items-center gap-2" data-testid="percentage-bar-refinanced">
        <div className="w-24 h-2 bg-blue-100 rounded-full overflow-hidden">
          <div className="h-full bg-blue-500 transition-all duration-300" style={{ width: '100%' }} />
        </div>
        <span className="text-xs font-medium text-blue-700 min-w-[45px] text-right">100.0%</span>
      </div>
    );
  }
  if (status === 'Paid in Full / Settled') {
    return (
      <div className="flex items-center gap-2" data-testid="percentage-bar-settled">
        <div className="w-24 h-2 bg-amber-100 rounded-full overflow-hidden">
          <div className="h-full bg-amber-500 transition-all duration-300" style={{ width: '100%' }} />
        </div>
        <span className="text-xs font-medium text-amber-700 min-w-[45px] text-right">100.0%</span>
      </div>
    );
  }

  // Standard collection-ratio scale
  const getColor = () => {
    if (percentage >= 100) return 'bg-emerald-500';
    if (percentage >= 75) return 'bg-green-500';
    if (percentage >= 50) return 'bg-yellow-500';
    if (percentage >= 25) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const getBgColor = () => {
    if (percentage >= 100) return 'bg-emerald-100';
    if (percentage >= 75) return 'bg-green-100';
    if (percentage >= 50) return 'bg-yellow-100';
    if (percentage >= 25) return 'bg-orange-100';
    return 'bg-red-100';
  };

  return (
    <div className="flex items-center gap-2" data-testid="percentage-bar-standard">
      <div className={`w-24 h-2 ${getBgColor()} rounded-full overflow-hidden`}>
        <div
          className={`h-full ${getColor()} transition-all duration-300`}
          style={{ width: `${Math.min(percentage, 100)}%` }}
        />
      </div>
      <span className="text-xs font-medium text-gray-700 min-w-[45px] text-right">
        {percentage.toFixed(1)}%
      </span>
    </div>
  );
};

export default PercentageBar;
